import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import { Button, Icon, Text } from '@pinpoint/design-kit';
import { GridWrapper } from 'src/components';
import { CellText } from 'src/components/cells';
import { fetchClientSftpFiles } from 'src/store/api';
import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';

const FOLDERS = [
  {
    id: 'actuarial',
    label: 'Actuarial',
    folder: 'actuarial',
    hint: 'CSV files uploaded to the actuarial folder.',
  },
  {
    id: 'hits',
    label: 'Classifier / Hits',
    folder: 'classifier/hits',
    hint: 'CSV files uploaded to classifier/hits.',
  },
  {
    id: 'validation',
    label: 'Classifier / Validation',
    folder: 'classifier/validation',
    hint: 'CSV files uploaded to classifier/validation.',
  },
  {
    id: 'miss',
    label: 'Classifier / Miss',
    folder: 'classifier/miss',
    hint: 'CSV files uploaded to classifier/miss.',
  },
  {
    id: 'predict',
    label: 'Predict',
    folder: 'predict',
    hint: 'CSV files uploaded to the predict folder.',
  },
];

function onboardingFilenameError(name) {
  if (!name.slice(-4).toLowerCase().includes('csv')) {
    return `File ${name} was not a csv file.`;
  }
  if (name.length > 60) {
    return 'File name may not exceed 60 characters total (including extension).';
  }
  return '';
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function datasetName(name) {
  return name.replaceAll(' ', '_').replace(/\.csv$/i, '');
}

/* eslint-disable react/prop-types */
function SelectFileCell({ row, column }) {
  const {
    original: { name },
  } = row;
  const checked = column.selectedName === name;
  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={`Select ${name}`}
      data-test="sftp-select-file"
      onClick={(event) => event.stopPropagation()}
      onChange={() => column.onToggle(name)}
    />
  );
}
/* eslint-enable react/prop-types */

function ModelActionButton({ name, icon, viewBox, tip, disabled, onClick }) {
  return (
    <span data-tip={tip}>
      <Button
        h="1.875rem"
        m={{ r: '1rem' }}
        p={{ x: '0.75rem' }}
        bg={disabled ? 'gray300' : 'navyBlue'}
        hoverBg={disabled ? 'gray300' : 'navyBlueDark'}
        disabled={disabled}
        onClick={onClick}
      >
        <Icon color="white" name={icon} viewBox={viewBox} size="1rem" m={{ r: '0.5rem' }} />
        {name}
      </Button>
    </span>
  );
}

ModelActionButton.propTypes = {
  name: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  viewBox: PropTypes.string.isRequired,
  tip: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

ModelActionButton.defaultProps = {
  disabled: false,
};

function FolderFiles({ folder, files, selectedName, onToggle, open, onOpen, getData, apiStatus }) {
  const rows = useMemo(
    () =>
      files.map((file) => ({
        name: file.name,
        size: formatSize(file.size),
        loadedAt: file.loadedAt ? dayjs(file.loadedAt).format('MMM DD, YYYY HH:mm') : '-',
      })),
    [files]
  );
  const columns = useMemo(
    () => [
      {
        Header: <Text textSize="small">Select</Text>,
        id: 'select',
        selectedName,
        onToggle,
        Cell: SelectFileCell,
      },
      {
        Header: <Text textSize="small">File</Text>,
        id: 'name',
        accessor: 'name',
        Cell: ({ value }) => CellText({ value }),
      },
      {
        Header: <Text textSize="small">Size</Text>,
        id: 'size',
        accessor: 'size',
        Cell: ({ value }) => CellText({ value }),
      },
      {
        Header: <Text textSize="small">Loaded</Text>,
        id: 'loadedAt',
        accessor: 'loadedAt',
        Cell: ({ value }) => CellText({ value }),
      },
    ],
    [selectedName, onToggle]
  );

  return (
    <div style={{ margin: '0 3.25rem 1rem', background: 'white', borderRadius: '0.375rem' }}>
      <button
        type="button"
        aria-expanded={open}
        data-test={`sftp-folder-${folder.id}`}
        onClick={onOpen}
        style={{
          background: 'none',
          border: 0,
          padding: '1rem 1.25rem',
          width: '100%',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Icon name={open ? 'DownArrow' : 'RightArrow'} size="1rem" />
          <Text textSize="subtitle" textWeight="500">
            {folder.label}
          </Text>
          <Text textSize="small">{files.length}</Text>
        </span>
        <span style={{ display: 'block', marginLeft: '1.5rem', marginTop: '0.35rem' }}>
          <Text textSize="small">{folder.hint}</Text>
        </span>
      </button>
      {open && rows.length === 0 && apiStatus === CONSTANTS.API.SUCCEEDED ? (
        <Text textSize="small" p={{ l: '2.75rem', b: '1rem' }}>
          No files
        </Text>
      ) : null}
      {open && (rows.length > 0 || apiStatus !== CONSTANTS.API.SUCCEEDED) ? (
        <GridWrapper
          hasData={rows.length > 0}
          getData={getData}
          columns={columns}
          data={rows}
          apiStatus={apiStatus}
          paginate={false}
          widths=".4fr 2fr .7fr 1.2fr"
        />
      ) : null}
    </div>
  );
}

FolderFiles.propTypes = {
  folder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    folder: PropTypes.string.isRequired,
    hint: PropTypes.string.isRequired,
  }).isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedName: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  getData: PropTypes.func.isRequired,
  apiStatus: PropTypes.string.isRequired,
};

FolderFiles.defaultProps = {
  selectedName: '',
};

function ClientSftpFiles({ tenant, enqueueNotification }) {
  const [files, setFiles] = useState([]);
  const [apiStatus, setApiStatus] = useState(CONSTANTS.API.STARTED);
  const [selected, setSelected] = useState({});
  const [open, setOpen] = useState({ actuarial: false, hits: false, validation: false, miss: false, predict: false });
  const client = useSelector((state) => state.app.client);
  const history = useHistory();

  const getData = useCallback(() => {
    if (!tenant) {
      setFiles([]);
      setApiStatus(CONSTANTS.API.SUCCEEDED);
      return;
    }
    setApiStatus(CONSTANTS.API.STARTED);
    fetchClientSftpFiles(tenant)
      .then((data) => {
        setFiles(data.items || []);
        setApiStatus(CONSTANTS.API.SUCCEEDED);
      })
      .catch((error) => {
        setFiles([]);
        setApiStatus(CONSTANTS.API.FAILED);
        enqueueNotification(error.message || 'Unable to load SFTP files');
      });
  }, [tenant, enqueueNotification]);

  useEffect(() => {
    getData();
  }, [getData]);

  const filesIn = useCallback((folder) => files.filter((file) => file.folder === folder), [files]);

  const chosen = useCallback(
    (folder) => files.find((file) => file.folder === folder && file.name === selected[folder]),
    [files, selected]
  );

  const onToggle = useCallback((folder, name) => {
    setSelected((current) => ({
      ...current,
      [folder]: current[folder] === name ? '' : name,
    }));
  }, []);

  const startSingle = useCallback(
    (folder, model, datasetTag) => {
      const file = chosen(folder);
      if (!file) return;
      const filenameError = onboardingFilenameError(file.name);
      if (filenameError) {
        enqueueNotification(filenameError);
        return;
      }
      history.push(ROUTES.NEW_ONBOARDING, {
        model,
        datasetName: datasetName(file.name),
        datasetTag,
        sftpFile: { tenant, name: file.name, folder: file.folder, size: file.size || 0 },
      });
    },
    [chosen, enqueueNotification, history, tenant]
  );

  const startClassifier = useCallback(() => {
    const datasetTagFor = {
      'classifier/hits': CONSTANTS.SEGMENT.TAG.HIT,
      'classifier/validation': CONSTANTS.SEGMENT.TAG.VALIDATION,
      'classifier/miss': CONSTANTS.SEGMENT.TAG.MISS,
    };
    const assigned = [chosen('classifier/hits'), chosen('classifier/validation'), chosen('classifier/miss')]
      .filter(Boolean)
      .map((file) => ({
        tenant,
        name: file.name,
        folder: file.folder,
        size: file.size || 0,
        datasetTag: datasetTagFor[file.folder],
      }));
    if (!assigned.length) return;
    const invalid = assigned.find((file) => onboardingFilenameError(file.name));
    if (invalid) {
      enqueueNotification(onboardingFilenameError(invalid.name));
      return;
    }
    const hit = assigned.find((file) => file.datasetTag === CONSTANTS.SEGMENT.TAG.HIT);
    const first = hit || assigned[0];
    history.push(ROUTES.NEW_ONBOARDING, {
      model: CONSTANTS.ONBOARDING_MODELS.CLASSIFIER_MODEL,
      datasetTag: CONSTANTS.SEGMENT.TAG.HIT,
      datasetName: datasetName(first.name),
      sftpFiles: assigned,
    });
  }, [chosen, enqueueNotification, history, tenant]);

  const actuarialFile = chosen('actuarial');
  const predictFile = chosen('predict');
  const classifierReady = Boolean(chosen('classifier/hits') && chosen('classifier/miss'));

  return (
    <>
      <Text textSize="small" textColor="navyBlue" p={{ l: '3.25rem', t: '1.5rem' }}>
        Files loaded by SFTP
      </Text>
      <Text textSize="small" p={{ l: '3.25rem', t: '.5rem' }}>
        Upload each CSV into actuarial, classifier/hits, classifier/validation, classifier/miss, or predict. The folder
        sets the file type.
      </Text>
      <div style={{ display: 'flex', padding: '1rem 3.25rem' }}>
        <ModelActionButton
          name="ACTUARIAL"
          icon="ActuarialModel"
          viewBox="0 0 12 10"
          tip="Use the selected actuarial file"
          disabled={!actuarialFile || !(client && client.canModel)}
          onClick={() =>
            startSingle('actuarial', CONSTANTS.ONBOARDING_MODELS.ACTUARIAL_MODEL, CONSTANTS.SEGMENT.TAG.ACTUARIAL)
          }
        />
        <ModelActionButton
          name="CLASSIFIER"
          icon="ClassifierModel"
          viewBox="0 0 10 16"
          tip="Use the selected Hits, Validation, and Miss files"
          disabled={!classifierReady || !(client && client.canModel)}
          onClick={startClassifier}
        />
        <ModelActionButton
          name="PREDICT"
          icon="ThinkalikeModel"
          viewBox="0 0 45 60"
          tip="Use the selected predict file"
          disabled={!predictFile || !(client && client.canScore)}
          onClick={() =>
            startSingle('predict', CONSTANTS.ONBOARDING_MODELS.PREDICT_MODEL, CONSTANTS.SEGMENT.TAG.TARGET)
          }
        />
      </div>
      {FOLDERS.map((folder) => (
        <FolderFiles
          key={folder.id}
          folder={folder}
          files={filesIn(folder.folder)}
          selectedName={selected[folder.folder] || ''}
          onToggle={(name) => onToggle(folder.folder, name)}
          open={open[folder.id]}
          onOpen={() => setOpen((current) => ({ ...current, [folder.id]: !current[folder.id] }))}
          getData={getData}
          apiStatus={apiStatus}
        />
      ))}
    </>
  );
}

ClientSftpFiles.propTypes = {
  tenant: PropTypes.string,
  enqueueNotification: PropTypes.func.isRequired,
};

ClientSftpFiles.defaultProps = {
  tenant: '',
};

export default ClientSftpFiles;
